import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  Add,
  Visibility,
  Edit,
  Block,
  Delete,
  CheckCircle,
  Cancel,
  AccountBalanceWallet,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import apiService from '../services/apiService';
import config from '../config/config';

const DriversPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [regions, setRegions] = useState([]);
  const [services, setServices] = useState([]);
  const [driverPhoto, setDriverPhoto] = useState(null);
  const [driverPhotoPreview, setDriverPhotoPreview] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  // URL of the bank-passbook image currently shown in the inline preview
  // dialog. null means dialog is closed.
  const [bankImagePreviewUrl, setBankImagePreviewUrl] = useState(null);

  // Recharge dialog state
  const [openRechargeDialog, setOpenRechargeDialog] = useState(false);
  const [rechargeDriver, setRechargeDriver] = useState(null);
  const [rechargePlans, setRechargePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [customDays, setCustomDays] = useState('');
  const [rechargeNotes, setRechargeNotes] = useState('');
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [driverRechargeStatus, setDriverRechargeStatus] = useState(null);

  const fetchDrivers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      console.log('Fetching drivers with params:', params);
      const response = await apiService.getDrivers(params);
      console.log('Drivers response:', response);

      if (response.success) {
        const driversData = response.data || [];
        console.log('Drivers data:', driversData);
        setDrivers(driversData);
        setError('');
      } else {
        // Empty data is not an error - just set empty array
        setDrivers([]);
        setError('');
      }
    } catch (err) {
      console.error('Drivers fetch error:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        code: err.code
      });
      setDrivers([]);

      // Check if it's a network error (no response from server)
      if (!err.response && (err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK' || err.message.includes('Network Error'))) {
        setError(`Unable to connect to backend server. Please ensure the backend is running on ${config.SOCKET_URL}`);
      } else if (err.response?.status === 404) {
        // 404 means endpoint not found or no data - don't show error
        setError('');
      } else if (err.response?.status === 401) {
        // Authentication error - will be handled by interceptor
        setError('');
      } else if (err.response) {
        // Other HTTP errors - only show if there's a meaningful error message
        const errorMessage = err.response.data?.message || err.message;
        if (errorMessage && !errorMessage.includes('No drivers')) {
          setError(errorMessage);
        } else {
          setError('');
        }
      } else {
        // For any other case (like empty data), don't show error
        setError('');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchRegions = async () => {
    try {
      const response = await apiService.getRegions();
      if (response.success) {
        setRegions(response.data.data || response.data);
      }
    } catch (err) {
      console.error('Regions error:', err);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await apiService.getServices();
      console.log('Services Response:', response);
      if (response.success) {
        const servicesData = response.data.data || response.data;
        console.log('Services Data:', servicesData);
        setServices(servicesData);
      }
    } catch (err) {
      console.error('Services error:', err);
    }
  };

  useEffect(() => {
    fetchDrivers();
    fetchRegions();
    fetchServices();
  }, [fetchDrivers]);

  const handleViewDriver = async (driver) => {
    setSelectedDriver(driver);
    setOpenDialog(true);
  };

  const handleBlockDriver = async (driverId) => {
    if (!window.confirm('Are you sure you want to block this driver?')) {
      return;
    }

    try {
      const response = await apiService.blockDriver(driverId, 'Blocked by admin');

      if (response.success) {
        alert('Driver blocked successfully');
        fetchDrivers();
      } else {
        alert(response.message || 'Failed to block driver');
      }
    } catch (err) {
      alert('Failed to block driver');
    }
  };

  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm('Are you sure you want to delete this driver? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.deleteDriver(driverId);

      if (response.success) {
        alert('Driver deleted successfully');
        fetchDrivers();
      } else {
        alert(response.message || 'Failed to delete driver');
      }
    } catch (err) {
      alert('Failed to delete driver');
    }
  };

  // Recharge handlers
  const handleOpenRechargeDialog = async (driver) => {
    setRechargeDriver(driver);
    setSelectedPlanId('');
    setCustomDays('');
    setRechargeNotes('');
    setOpenRechargeDialog(true);

    // Fetch recharge plans (filtered by driver's service type) and driver's current recharge status
    try {
      const [plansResponse, statusResponse] = await Promise.all([
        apiService.getAdminRechargePlans(driver.id), // Pass driver ID to filter plans by service
        apiService.getDriverRechargeStatus(driver.id)
      ]);

      if (plansResponse.success) {
        setRechargePlans(plansResponse.data || []);
      }

      if (statusResponse.success) {
        setDriverRechargeStatus(statusResponse.data);
      }
    } catch (err) {
      console.error('Error fetching recharge data:', err);
    }
  };

  const handleCloseRechargeDialog = () => {
    setOpenRechargeDialog(false);
    setRechargeDriver(null);
    setRechargePlans([]);
    setSelectedPlanId('');
    setCustomDays('');
    setRechargeNotes('');
    setDriverRechargeStatus(null);
  };

  const handleAssignRecharge = async () => {
    if (!selectedPlanId && !customDays) {
      alert('Please select a plan or enter custom days');
      return;
    }

    if (customDays && (isNaN(customDays) || parseInt(customDays) < 1)) {
      alert('Please enter a valid number of days');
      return;
    }

    setRechargeLoading(true);

    try {
      const data = {
        notes: rechargeNotes || 'Assigned by admin'
      };

      if (selectedPlanId) {
        data.planId = selectedPlanId;
      } else {
        data.days = parseInt(customDays);
        data.amount = 0; // Free assignment by admin
      }

      const response = await apiService.assignDriverRecharge(rechargeDriver.id, data);

      if (response.success) {
        alert(`Recharge assigned successfully! ${response.data?.rechargeDays || customDays} days added.`);
        handleCloseRechargeDialog();
        fetchDrivers();
      } else {
        alert(response.message || 'Failed to assign recharge');
      }
    } catch (err) {
      console.error('Assign recharge error:', err);
      alert('Failed to assign recharge');
    } finally {
      setRechargeLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: editMode && editingDriver ? editingDriver.name : '',
      phone: editMode && editingDriver ? editingDriver.phone : '',
      gender: editMode && editingDriver ? editingDriver.gender : '',
      region: editMode && editingDriver ? editingDriver.region_id : '',
      vehicleType: editMode && editingDriver ? editingDriver.vehicleTypeId : '',
      vehicleNumber: editMode && editingDriver ? editingDriver.vehicleNumber : '',
      vehicleModel: editMode && editingDriver ? editingDriver.vehicleModel : '',
      status: editMode && editingDriver ? editingDriver.status : 'pending',
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required').min(3, 'Name must be at least 3 characters'),
      phone: Yup.string()
        .required('Phone number is required')
        .matches(/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/im, 'Invalid phone number'),
      gender: Yup.string().required('Gender is required'),
      region: Yup.string().required('Region is required'),
      vehicleType: Yup.string().required('Vehicle Type is required'),
      vehicleNumber: Yup.string().required('Vehicle Number is required'),
      vehicleModel: Yup.string().required('Vehicle Model is required'),
      status: Yup.string().required('Status is required'),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        let response;

        if (editMode) {
          // Edit mode - update driver
          const updateData = {
            name: values.name,
            phone: values.phone,
            gender: values.gender,
            region: values.region,
            vehicleType: values.vehicleType,
            vehicleNumber: values.vehicleNumber,
            vehicleModel: values.vehicleModel,
            status: values.status,
          };

          response = await apiService.updateDriver(editingDriver.id, updateData);

          if (response.success) {
            alert('Driver updated successfully');
            setOpenAddDialog(false);
            setEditMode(false);
            setEditingDriver(null);
            resetForm();
            fetchDrivers();
          } else {
            const errorMsg = response.message || response.error?.message || 'Failed to update driver';
            alert(errorMsg);
          }
        } else {
          // Add mode - validate photo is uploaded
          if (!driverPhoto) {
            alert('Please upload a driver photo');
            setSubmitting(false);
            return;
          }

          // Create FormData for file upload
          const formData = new FormData();
          formData.append('name', values.name);
          formData.append('phone', values.phone);
          formData.append('gender', values.gender);
          formData.append('region', values.region);
          formData.append('vehicleType', values.vehicleType);
          formData.append('vehicleNumber', values.vehicleNumber);
          formData.append('vehicleModel', values.vehicleModel);
          formData.append('status', values.status);
          formData.append('photo', driverPhoto);

          console.log('Submitting driver with FormData...');
          response = await apiService.addDriver(formData);
          console.log('Add driver response:', response);

          if (response.success) {
            alert('Driver added successfully');
            setOpenAddDialog(false);
            resetForm();
            setDriverPhoto(null);
            setDriverPhotoPreview(null);
            fetchDrivers();
          } else {
            const errorMsg = response.message || response.error?.message || 'Failed to add driver';
            console.error('Failed to add driver:', errorMsg, response);
            alert(errorMsg);
          }
        }
      } catch (err) {
        console.error('Driver operation error:', err);
        console.error('Error details:', err.response || err);
        const errorMsg = err.response?.data?.message || err.message || 'Operation failed';
        alert('Error: ' + errorMsg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleAddDriver = () => {
    setEditMode(false);
    setEditingDriver(null);
    formik.resetForm();
    setDriverPhoto(null);
    setDriverPhotoPreview(null);
    setOpenAddDialog(true);
  };

  const handleEditDriver = (driver) => {
    setEditMode(true);
    setEditingDriver(driver);
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setEditMode(false);
    setEditingDriver(null);
    formik.resetForm();
    setDriverPhoto(null);
    setDriverPhotoPreview(null);
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should not exceed 5MB');
        return;
      }
      setDriverPhoto(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setDriverPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: 'Active', color: 'success' },
      pending: { label: 'Pending', color: 'warning' },
      blocked: { label: 'Blocked', color: 'error' },
      inactive: { label: 'Inactive', color: 'default' },
    };

    const config = statusConfig[status] || statusConfig.inactive;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getVerificationChip = (status) => {
    const statusConfig = {
      verified: { label: 'Verified', color: 'success', icon: <CheckCircle /> },
      pending: { label: 'Pending', color: 'warning' },
      rejected: { label: 'Rejected', color: 'error', icon: <Cancel /> },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Chip label={config.label} color={config.color} size="small" icon={config.icon} />;
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Driver Name',
      width: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.name || '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.phone}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'vehicleType',
      headerName: 'Vehicle',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.row.vehicleType}</Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.vehicleNumber}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => getStatusChip(params.row.status),
    },
    {
      field: 'verificationStatus',
      headerName: 'Verification',
      width: 130,
      renderCell: (params) => getVerificationChip(params.row.verificationStatus),
    },
    {
      field: 'rating',
      headerName: 'Rating',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          ⭐ {Number(params.row.rating || 0).toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'totalRides',
      headerName: 'Total Rides',
      width: 110,
    },
    {
      field: 'isOnline',
      headerName: 'Online',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.isOnline ? 'Online' : 'Offline'}
          color={params.row.isOnline ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'walletBalance',
      headerName: 'Wallet',
      width: 110,
      renderCell: (params) => `₹${params.row.walletBalance}`,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleViewDriver(params.row)}>
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEditDriver(params.row)}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            sx={{ color: '#FF9800' }}
            onClick={() => handleOpenRechargeDialog(params.row)}
            title="Assign Recharge"
          >
            <AccountBalanceWallet fontSize="small" />
          </IconButton>
          {params.row.status !== 'blocked' && (
            <IconButton
              size="small"
              color="error"
              onClick={() => handleBlockDriver(params.row.id)}
            >
              <Block fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteDriver(params.row.id)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const filteredDrivers = drivers.filter((driver) =>
    driver.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    driver.phone.includes(searchQuery) ||
    driver.vehicleNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Drivers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all driver accounts
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddDriver}
          sx={{
            bgcolor: '#FFC107',
            color: '#000',
            '&:hover': {
              bgcolor: '#FFD54F',
            },
          }}
        >
          Add Driver
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              placeholder="Search by name, phone, or vehicle number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <DataGrid
            rows={filteredDrivers}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
            localeText={{
              noRowsLabel: 'No drivers found. Click "Add Driver" to create your first driver.',
            }}
          />
        </CardContent>
      </Card>

      {/* View Driver Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Driver Details</DialogTitle>
        <DialogContent>
          {selectedDriver && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedDriver.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedDriver.phone}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedDriver.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Vehicle Type</Typography>
                  <Typography variant="body1">{selectedDriver.vehicleType}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Vehicle Number</Typography>
                  <Typography variant="body1">{selectedDriver.vehicleNumber}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedDriver.status)}</Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Verification</Typography>
                  <Box sx={{ mt: 0.5 }}>{getVerificationChip(selectedDriver.verificationStatus)}</Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Rating</Typography>
                  <Typography variant="body1">⭐ {Number(selectedDriver.rating || 0).toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Total Rides</Typography>
                  <Typography variant="body1">{selectedDriver.totalRides}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Wallet Balance</Typography>
                  <Typography variant="body1">₹{selectedDriver.walletBalance}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Subscription Expiry</Typography>
                  <Typography variant="body1">
                    {selectedDriver.subscriptionExpiry || 'No active subscription'}
                  </Typography>
                </Grid>

                {/* Bank Details (optional — driver may submit this so we can
                    credit them manually for refunds, fare disputes, etc.) */}
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, mb: 1, pt: 2, borderTop: '1px solid #eee' }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Bank Details
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Use these to manually credit the driver if needed.
                    </Typography>
                  </Box>
                </Grid>
                {(selectedDriver.bank_account_number || selectedDriver.bankAccountNumber ||
                  selectedDriver.bank_ifsc_code || selectedDriver.bankIfscCode ||
                  selectedDriver.bank_account_image || selectedDriver.bankAccountImage) ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Account Number</Typography>
                      <Typography variant="body1" fontWeight={600}
                        sx={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>
                        {selectedDriver.bank_account_number ||
                          selectedDriver.bankAccountNumber ||
                          'Not provided'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">IFSC Code</Typography>
                      <Typography variant="body1" fontWeight={600}
                        sx={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>
                        {selectedDriver.bank_ifsc_code ||
                          selectedDriver.bankIfscCode ||
                          'Not provided'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Passbook / Cheque Image
                      </Typography>
                      {(() => {
                        const imgPath = selectedDriver.bank_account_image || selectedDriver.bankAccountImage;
                        if (!imgPath) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              No image uploaded
                            </Typography>
                          );
                        }
                        const cleanPath = String(imgPath).trim().replace(/^\/+/, '');
                        const baseUrl = String(config.SOCKET_URL).replace(/\/+$/, '');
                        const fullUrl = `${baseUrl}/${cleanPath}`;
                        return (
                          <Box
                            onClick={() => setBankImagePreviewUrl(fullUrl)}
                            sx={{ display: 'inline-block', cursor: 'pointer' }}
                          >
                            {/* Plain <img> tag — Box component="img" was
                                rendering broken in some browsers. */}
                            <img
                              src={fullUrl}
                              alt="Bank passbook"
                              style={{
                                maxWidth: 300,
                                maxHeight: 200,
                                display: 'block',
                                borderRadius: 4,
                                border: '1px solid #e0e0e0',
                              }}
                            />
                            <Typography variant="caption" display="block"
                              sx={{ mt: 0.5, color: 'primary.main' }}>
                              Click to view full-size
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary"
                      sx={{ fontStyle: 'italic' }}>
                      Driver has not provided bank details yet (this is optional).
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Bank Passbook Image Preview Dialog (inline — no new tab) */}
      <Dialog
        open={Boolean(bankImagePreviewUrl)}
        onClose={() => setBankImagePreviewUrl(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Bank Passbook / Cheque
          <IconButton size="small" onClick={() => setBankImagePreviewUrl(null)}>
            <Cancel />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {bankImagePreviewUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
              <img
                src={bankImagePreviewUrl}
                alt="Bank passbook full size"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  borderRadius: 4,
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBankImagePreviewUrl(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Driver Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Driver' : 'Add Driver'}</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  placeholder="Enter Full Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  placeholder="+91 9876543210"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  error={formik.touched.gender && Boolean(formik.errors.gender)}
                >
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formik.values.gender}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Gender"
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                  {formik.touched.gender && formik.errors.gender && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {formik.errors.gender}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  error={formik.touched.region && Boolean(formik.errors.region)}
                >
                  <InputLabel>Region</InputLabel>
                  <Select
                    name="region"
                    value={formik.values.region}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Region"
                  >
                    {regions.map((region) => (
                      <MenuItem key={region.id} value={region.id}>
                        {region.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.region && formik.errors.region && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {formik.errors.region}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  error={formik.touched.vehicleType && Boolean(formik.errors.vehicleType)}
                >
                  <InputLabel>Vehicle Type</InputLabel>
                  <Select
                    name="vehicleType"
                    value={formik.values.vehicleType}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Vehicle Type"
                  >
                    {services.length === 0 ? (
                      <MenuItem disabled>No services available. Please add services first.</MenuItem>
                    ) : (
                      services.map((service) => (
                        <MenuItem key={service.id} value={service.id}>
                          {service.service_name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {formik.touched.vehicleType && formik.errors.vehicleType && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {formik.errors.vehicleType}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Vehicle Number"
                  name="vehicleNumber"
                  placeholder="KA 01 AB 1234"
                  value={formik.values.vehicleNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.vehicleNumber && Boolean(formik.errors.vehicleNumber)}
                  helperText={formik.touched.vehicleNumber && formik.errors.vehicleNumber}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Vehicle Model"
                  name="vehicleModel"
                  placeholder="Honda City"
                  value={formik.values.vehicleModel}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.vehicleModel && Boolean(formik.errors.vehicleModel)}
                  helperText={formik.touched.vehicleModel && formik.errors.vehicleModel}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  error={formik.touched.status && Boolean(formik.errors.status)}
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="blocked">Blocked</MenuItem>
                  </Select>
                  {formik.touched.status && formik.errors.status && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {formik.errors.status}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Driver Photo *
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    sx={{
                      borderColor: '#FFC107',
                      color: '#000',
                      '&:hover': {
                        borderColor: '#FFD54F',
                        bgcolor: 'rgba(255, 193, 7, 0.1)',
                      },
                    }}
                  >
                    Choose Photo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handlePhotoChange}
                    />
                  </Button>
                  {driverPhotoPreview && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        component="img"
                        src={driverPhotoPreview}
                        alt="Driver preview"
                        sx={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 2,
                          border: '2px solid #FFC107',
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {driverPhoto?.name}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
              sx={{
                bgcolor: '#FFC107',
                color: '#000',
                '&:hover': {
                  bgcolor: '#FFD54F',
                },
              }}
            >
              {editMode ? 'Update Driver' : 'Add Driver'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Assign Recharge Dialog */}
      <Dialog open={openRechargeDialog} onClose={handleCloseRechargeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceWallet sx={{ color: '#FF9800' }} />
            Assign Recharge Plan
          </Box>
        </DialogTitle>
        <DialogContent>
          {rechargeDriver && (
            <Box sx={{ pt: 2 }}>
              {/* Driver Info */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {rechargeDriver.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {rechargeDriver.phone} • {rechargeDriver.vehicleType}
                </Typography>
              </Box>

              {/* Current Recharge Status */}
              {driverRechargeStatus && (
                <Alert
                  severity={driverRechargeStatus.hasActiveRecharge ? 'success' : 'warning'}
                  sx={{ mb: 3 }}
                >
                  {driverRechargeStatus.hasActiveRecharge ? (
                    <>
                      Active recharge: {driverRechargeStatus.remainingDays} days remaining
                      <br />
                      <Typography variant="caption">
                        Expires: {new Date(driverRechargeStatus.subscriptionExpiry).toLocaleDateString()}
                      </Typography>
                    </>
                  ) : (
                    'No active recharge plan'
                  )}
                </Alert>
              )}

              {/* Select Plan */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Recharge Plan</InputLabel>
                <Select
                  value={selectedPlanId}
                  onChange={(e) => {
                    setSelectedPlanId(e.target.value);
                    setCustomDays(''); // Clear custom days when plan is selected
                  }}
                  label="Select Recharge Plan"
                >
                  <MenuItem value="">
                    <em>-- Select a plan or enter custom days below --</em>
                  </MenuItem>
                  {rechargePlans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.name || `${plan.days} Days`} - ₹{plan.price} ({plan.days} days)
                      {plan.service_name && ` • ${plan.service_name}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* OR Divider */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ flex: 1, height: 1, bgcolor: '#ddd' }} />
                <Typography sx={{ px: 2, color: 'text.secondary' }}>OR</Typography>
                <Box sx={{ flex: 1, height: 1, bgcolor: '#ddd' }} />
              </Box>

              {/* Custom Days */}
              <TextField
                fullWidth
                label="Custom Days (Free Assignment)"
                type="number"
                value={customDays}
                onChange={(e) => {
                  setCustomDays(e.target.value);
                  setSelectedPlanId(''); // Clear plan when custom days is entered
                }}
                placeholder="Enter number of days"
                inputProps={{ min: 1 }}
                sx={{ mb: 3 }}
                helperText="Enter custom days for free admin assignment"
              />

              {/* Notes */}
              <TextField
                fullWidth
                label="Notes (Optional)"
                value={rechargeNotes}
                onChange={(e) => setRechargeNotes(e.target.value)}
                placeholder="e.g., Promotional offer, Compensation, etc."
                multiline
                rows={2}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRechargeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignRecharge}
            disabled={rechargeLoading || (!selectedPlanId && !customDays)}
            sx={{
              bgcolor: '#FF9800',
              color: '#fff',
              '&:hover': {
                bgcolor: '#F57C00',
              },
            }}
          >
            {rechargeLoading ? 'Assigning...' : 'Assign Recharge'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriversPage;
